import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  getTemplateSetsByProject, getCitiesByProject,
  getGenerationPresetsByProject, createGenerationPreset, updateGenerationPreset, deleteGenerationPreset,
  type TemplateSet, type DataRow,
} from '@/lib/api';
import { replacePlaceholders } from '@/lib/generator';
import type { CustomGroup, CampaignStructureOverride, GenerationPreset, GenerationPresetConfig, BudgetConfig } from '@/types';
import { Zap, FileText, AlertCircle, ChevronRight, ChevronDown, Search, X, Filter, Plus, Trash2, MapPin, Settings2, Bookmark, Check, Globe, Pin, Ban, DollarSign } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/app/components/ui/select';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Badge } from '@/app/components/ui/badge';
import { Checkbox } from '@/app/components/ui/checkbox';
import { Switch } from '@/app/components/ui/switch';
import { toast } from 'sonner';

const HIDDEN_COLS = new Set(['id', 'project_id']);

function isComplete(ts: TemplateSet): boolean {
  const c = ts.template_content;
  return (
    (c.headlines || []).length >= 15 &&
    (c.headlines || []).every(s => s.variants.some(v => v.enabled && v.template_string.trim())) &&
    (c.descriptions || []).length >= 4 &&
    (c.descriptions || []).every(s => s.variants.some(v => v.enabled && v.template_string.trim())) &&
    (c.keywords || []).length > 0
  );
}

interface CustomGroupDraft {
  id: string;
  name: string;
  campaignNameOverride: string;
  useOverride: boolean;
  rows: DataRow[];
  searchQuery: string;
}

export default function GenerationView() {
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  const [templateSets, setTemplateSets] = useState<TemplateSet[]>([]);
  const [rows, setRows] = useState<DataRow[]>([]);
  const [selectedTemplateSetIds, setSelectedTemplateSetIds] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);

  // Filter state
  const [filterColumn, setFilterColumn] = useState<string>('');
  const [selectedValues, setSelectedValues] = useState<Set<string>>(new Set());
  const [valueSearch, setValueSearch] = useState('');
  const [bulkEnabled, setBulkEnabled] = useState(true);

  // Structure override state
  const [overrideEnabled, setOverrideEnabled] = useState(false);
  const [structureOverride, setStructureOverride] = useState<CampaignStructureOverride>({});

  // Presets state
  const [presets, setPresets] = useState<GenerationPreset[]>([]);
  const [loadedPresetId, setLoadedPresetId] = useState<string | null>(null);
  const [presetName, setPresetName] = useState('');
  const [isSavingPreset, setIsSavingPreset] = useState(false);
  const [showPresetDropdown, setShowPresetDropdown] = useState(false);
  const presetDropdownRef = useRef<HTMLDivElement>(null);

  // Budget state
  const [budgetConfig, setBudgetConfig] = useState<BudgetConfig>({
    enabled: false,
    mode: 'uniform',
  });

  // Excluded rows state
  const [excludedRows, setExcludedRows] = useState<Set<DataRow>>(new Set());
  const [excludeSearchQuery, setExcludeSearchQuery] = useState('');
  const [showExcludeSearch, setShowExcludeSearch] = useState(false);
  const excludeSearchRef = useRef<HTMLDivElement>(null);

  // Custom groups state
  const [customGroups, setCustomGroups] = useState<CustomGroupDraft[]>([]);
  const [openGroupSearchId, setOpenGroupSearchId] = useState<string | null>(null);
  const searchRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // UI state
  const [targetingTab, setTargetingTab] = useState<'all' | 'pinned'>('all');
  const [showCampaignSetup, setShowCampaignSetup] = useState(false);
  const suppressFilterResetRef = useRef(false);

  // Cursor position refs for placeholder insertion
  const campaignNameInputRef = useRef<HTMLInputElement>(null);
  const adGroupNameInputRef = useRef<HTMLInputElement>(null);
  const campaignNameCursorRef = useRef<number | null>(null);
  const adGroupNameCursorRef = useRef<number | null>(null);

  useEffect(() => {
    if (!projectId) return;
    setIsLoading(true);
    Promise.all([
      getTemplateSetsByProject(projectId),
      getCitiesByProject(projectId),
      getGenerationPresetsByProject(projectId).catch(() => [] as GenerationPreset[]),
    ])
      .then(([templateSetsData, rowsData, presetsData]) => {
        setTemplateSets(templateSetsData);
        setRows(rowsData);
        setPresets(presetsData);
        const first = templateSetsData.find(isComplete) ?? templateSetsData[0];
        if (first) setSelectedTemplateSetIds(new Set([first.id]));
      })
      .catch(() => toast.error('Failed to load data'))
      .finally(() => setIsLoading(false));
  }, [projectId]);

  // One document-level mousedown listener handles all open dropdowns. Consolidating
  // avoids re-registering three separate listeners and the rare race where one
  // handler's setState fires before another dropdown's onClick.
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (openGroupSearchId) {
        const ref = searchRefs.current[openGroupSearchId];
        if (ref && !ref.contains(target)) setOpenGroupSearchId(null);
      }
      if (showExcludeSearch && excludeSearchRef.current && !excludeSearchRef.current.contains(target)) {
        setShowExcludeSearch(false);
      }
      if (showPresetDropdown && presetDropdownRef.current && !presetDropdownRef.current.contains(target)) {
        setShowPresetDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [openGroupSearchId, showExcludeSearch, showPresetDropdown]);

  // Columns available for filtering
  const columns = useMemo(() => {
    if (rows.length === 0) return [];
    return Object.keys(rows[0]).filter(k => !HIDDEN_COLS.has(k));
  }, [rows]);

  // Value counts for the selected column
  const valueCounts = useMemo(() => {
    if (!filterColumn) return new Map<string, number>();
    const map = new Map<string, number>();
    rows.forEach(row => {
      const v = String(row[filterColumn] ?? '');
      map.set(v, (map.get(v) ?? 0) + 1);
    });
    return map;
  }, [rows, filterColumn]);

  const sortedValues = useMemo(() =>
    Array.from(valueCounts.entries()).sort((a, b) => a[0].localeCompare(b[0])),
  [valueCounts]);

  const visibleValues = useMemo(() => {
    const q = valueSearch.trim().toLowerCase();
    return q ? sortedValues.filter(([v]) => v.toLowerCase().includes(q)) : sortedValues;
  }, [sortedValues, valueSearch]);

  // When column changes, select all values by default (unless loading a preset)
  useEffect(() => {
    if (suppressFilterResetRef.current) {
      suppressFilterResetRef.current = false;
      return;
    }
    if (filterColumn) {
      setSelectedValues(new Set(Array.from(valueCounts.keys())));
      setValueSearch('');
    } else {
      setSelectedValues(new Set());
    }
  }, [filterColumn, valueCounts]);

  const allSelected = selectedValues.size === valueCounts.size;
  const noneSelected = selectedValues.size === 0;

  const filteredRows = useMemo(() => {
    if (!filterColumn || allSelected) return rows;
    return rows.filter(row => selectedValues.has(String(row[filterColumn] ?? '')));
  }, [rows, filterColumn, selectedValues, allSelected]);

  const toggleValue = (v: string) => {
    setSelectedValues(prev => {
      const next = new Set(prev);
      next.has(v) ? next.delete(v) : next.add(v);
      return next;
    });
  };

  const selectedTemplates = templateSets.filter(ts => selectedTemplateSetIds.has(ts.id));
  const selectedTemplate = selectedTemplates[0] ?? null;

  // ── Custom groups helpers ──

  const getRowLabel = (row: DataRow): string => {
    const fields = selectedTemplate?.template_content.selected_fields;
    if (fields && fields.length > 0) {
      const vals = fields.slice(0, 2).map(f => {
        const v = f.startsWith('custom:') ? row.custom_fields?.[f.replace('custom:', '')] : row[f];
        return String(v ?? '');
      }).filter(Boolean);
      if (vals.length > 0) return vals.join(' · ');
    }
    const vals = columns.slice(0, 3).map(c => String(row[c] ?? '')).filter(Boolean);
    return vals.slice(0, 2).join(' · ') || 'Row';
  };

  const searchRowAllFields = (row: DataRow, q: string): boolean =>
    Object.entries(row).some(([key, val]) => {
      if (key === 'custom_fields' && val && typeof val === 'object') {
        return Object.values(val).some(cv => String(cv ?? '').toLowerCase().includes(q));
      }
      return typeof val === 'string' && val.toLowerCase().includes(q);
    });

  const rowMatchHint = (row: DataRow, query: string): string | null => {
    if (!query.trim()) return null;
    const q = query.toLowerCase();
    const label = getRowLabel(row).toLowerCase();
    if (label.includes(q)) return null;
    for (const [key, val] of Object.entries(row)) {
      if (key === 'custom_fields' && val && typeof val === 'object') {
        for (const cv of Object.values(val)) {
          const s = String(cv ?? '');
          if (s.toLowerCase().includes(q)) return s;
        }
        continue;
      }
      if (typeof val === 'string' && val.toLowerCase().includes(q)) return val;
    }
    return null;
  };

  const getSearchResults = (query: string, groupId: string): DataRow[] => {
    if (!query.trim()) return [];
    const q = query.toLowerCase();
    const alreadyInGroup = new Set(customGroups.find(g => g.id === groupId)?.rows ?? []);
    return rows.filter(row => {
      if (alreadyInGroup.has(row)) return false;
      return searchRowAllFields(row, q);
    }).slice(0, 8);
  };

  const addCustomGroup = () => {
    setCustomGroups(prev => [...prev, {
      id: crypto.randomUUID(),
      name: `Campaign ${prev.length + 1}`,
      campaignNameOverride: '',
      useOverride: false,
      rows: [],
      searchQuery: '',
    }]);
    setTargetingTab('pinned');
  };

  const updateGroup = (id: string, updates: Partial<CustomGroupDraft>) => {
    setCustomGroups(prev => prev.map(g => g.id === id ? { ...g, ...updates } : g));
  };

  const removeGroup = (id: string) => {
    setCustomGroups(prev => prev.filter(g => g.id !== id));
  };

  const addRowToGroup = (groupId: string, row: DataRow) => {
    setCustomGroups(prev => prev.map(g =>
      g.id === groupId ? { ...g, rows: [...g.rows, row], searchQuery: '' } : g
    ));
    setOpenGroupSearchId(null);
  };

  const removeRowFromGroup = (groupId: string, row: DataRow) => {
    setCustomGroups(prev => prev.map(g =>
      g.id === groupId ? { ...g, rows: g.rows.filter(r => r !== row) } : g
    ));
  };

  // ── Exclude row helpers ──

  const getExcludeSearchResults = (query: string): DataRow[] => {
    if (!query.trim()) return [];
    const q = query.toLowerCase();
    return filteredRows.filter(row => {
      if (excludedRows.has(row)) return false;
      return searchRowAllFields(row, q);
    }).slice(0, 8);
  };

  const excludeRow = (row: DataRow) => {
    setExcludedRows(prev => new Set([...prev, row]));
    setExcludeSearchQuery('');
    setShowExcludeSearch(false);
  };

  const unexcludeRow = (row: DataRow) => {
    setExcludedRows(prev => {
      const next = new Set(prev);
      next.delete(row);
      return next;
    });
  };

  // ── Derived ──

  const customGroupRowSet = useMemo(
    () => new Set(customGroups.flatMap(g => g.rows)),
    [customGroups]
  );

  const bulkRows = useMemo(
    () => bulkEnabled ? filteredRows.filter(r => !customGroupRowSet.has(r) && !excludedRows.has(r)) : [],
    [filteredRows, customGroupRowSet, excludedRows, bulkEnabled]
  );

  const totalCustomGroupRows = useMemo(
    () => customGroups.reduce((acc, g) => acc + g.rows.length, 0),
    [customGroups]
  );

  // Derive unique campaign names for per-campaign budgeting
  const uniqueCampaignNames = useMemo(() => {
    const groupingField = overrideEnabled
      ? structureOverride.primary_grouping_field
      : (selectedTemplate?.template_content.primary_grouping_field
          ?? selectedTemplate?.template_content.campaign_grouping_field);
    const nameTemplate = overrideEnabled
      ? structureOverride.campaign_name_template
      : selectedTemplate?.template_content.campaign_name_template;
    if (!groupingField || !nameTemplate) return [];
    const seen = new Set<string>();
    const names: string[] = [];
    for (const row of bulkRows) {
      const tp = selectedTemplate?.template_content.template_placeholders ?? {};
      const resolved = replacePlaceholders(nameTemplate, row, tp);
      if (!seen.has(resolved)) {
        seen.add(resolved);
        names.push(resolved);
      }
    }
    return names.sort();
  }, [bulkRows, overrideEnabled, structureOverride, selectedTemplate]);

  const canGenerate = selectedTemplates.length > 0 && (bulkRows.length > 0 || totalCustomGroupRows > 0);

  const handleGeneratePreview = () => {
    if (selectedTemplates.length === 0) return;
    const cleanGroups: CustomGroup[] = customGroups
      .filter(g => g.rows.length > 0)
      .map(g => ({
        id: g.id,
        name: g.name,
        campaignNameOverride: g.useOverride && g.campaignNameOverride.trim() ? g.campaignNameOverride.trim() : undefined,
        rows: g.rows,
      }));
    navigate(`/projects/${projectId}/generate/preview`, {
      state: {
        generations: selectedTemplates.map(ts => ({
          templateSet: ts,
          rows: bulkRows,
          customGroups: cleanGroups,
        })),
        ...(overrideEnabled && Object.values(structureOverride).some(v => v) ? { structureOverride } : {}),
        ...(budgetConfig.enabled ? { budgetConfig } : {}),
      },
    });
  };

  // ── Preset helpers ──

  const buildPresetConfig = (): GenerationPresetConfig => ({
    template_set_ids: Array.from(selectedTemplateSetIds),
    structure_override: overrideEnabled ? structureOverride : undefined,
    override_enabled: overrideEnabled,
    bulk_enabled: bulkEnabled,
    filter_column: filterColumn || undefined,
    filter_values: filterColumn ? Array.from(selectedValues) : undefined,
    excluded_rows: excludedRows.size > 0 ? Array.from(excludedRows) : undefined,
    budget_config: budgetConfig.enabled ? budgetConfig : undefined,
    custom_groups: customGroups.filter(g => g.rows.length > 0).map(g => ({
      name: g.name,
      campaign_name_override: g.useOverride && g.campaignNameOverride.trim() ? g.campaignNameOverride.trim() : undefined,
      use_override: g.useOverride,
      row_data: g.rows,
    })),
  });

  const applyPresetConfig = (config: GenerationPresetConfig) => {
    setSelectedTemplateSetIds(new Set(config.template_set_ids));
    setOverrideEnabled(config.override_enabled);
    setStructureOverride(config.structure_override ?? {});
    setBulkEnabled(config.bulk_enabled);
    if (config.filter_column && config.filter_values) {
      suppressFilterResetRef.current = true;
      setFilterColumn(config.filter_column);
      setSelectedValues(new Set(config.filter_values));
    } else {
      setFilterColumn('');
    }
    // Match a saved row (plain JSON object from preset) back to a live row by column equality.
    // Critical: custom-group and excluded-row Sets use object reference identity, so we must
    // resolve restored rows back to the live `rows` array. Otherwise rows leak into bulk
    // generation alongside their custom group.
    const matchRow = (savedRow: Record<string, any>): DataRow | undefined =>
      rows.find(r => columns.every(col => String(r[col] ?? '') === String(savedRow[col] ?? '')));

    if (config.excluded_rows && config.excluded_rows.length > 0) {
      setExcludedRows(new Set(config.excluded_rows.map(matchRow).filter((r): r is DataRow => r != null)));
    } else {
      setExcludedRows(new Set());
    }
    // Restore budget config
    if (config.budget_config) {
      setBudgetConfig(config.budget_config);
    } else {
      setBudgetConfig({ enabled: false, mode: 'uniform' });
    }
    if (config.custom_groups.length > 0) {
      const rowById = new Map(rows.map(r => [String(r.id ?? ''), r]));
      setCustomGroups(config.custom_groups.map(cg => ({
        id: crypto.randomUUID(),
        name: cg.name,
        campaignNameOverride: cg.campaign_name_override ?? '',
        useOverride: cg.use_override,
        rows: cg.row_data && cg.row_data.length > 0
          ? cg.row_data.map(matchRow).filter((r): r is DataRow => r != null)
          : (cg.row_ids ?? []).map(id => rowById.get(id)).filter((r): r is DataRow => r != null),
        searchQuery: '',
      })));
    } else {
      setCustomGroups([]);
    }
  };

  const handleSavePreset = async (updateExisting: boolean) => {
    if (!projectId || !presetName.trim()) return;
    setIsSavingPreset(true);
    try {
      const config = buildPresetConfig();
      if (updateExisting && loadedPresetId) {
        await updateGenerationPreset(loadedPresetId, { name: presetName.trim(), config });
        toast.success(`Preset "${presetName}" updated`);
      } else {
        const saved = await createGenerationPreset({ project_id: projectId, name: presetName.trim(), config });
        setLoadedPresetId(saved.id);
        toast.success(`Preset "${presetName}" saved`);
      }
      getGenerationPresetsByProject(projectId).then(setPresets).catch(() => {});
    } catch (_) {
      toast.error('Failed to save preset');
    } finally {
      setIsSavingPreset(false);
    }
  };

  const loadPreset = (preset: GenerationPreset) => {
    applyPresetConfig(preset.config);
    setLoadedPresetId(preset.id);
    setPresetName(preset.name);
    setShowPresetDropdown(false);
    toast.success(`Loaded "${preset.name}"`);
  };

  const handleDeletePreset = async (presetId: string) => {
    if (!projectId) return;
    try {
      await deleteGenerationPreset(presetId);
      if (loadedPresetId === presetId) { setLoadedPresetId(null); setPresetName(''); }
      setPresets(prev => prev.filter(p => p.id !== presetId));
      toast.success('Preset deleted');
    } catch (_) {
      toast.error('Failed to delete preset');
    }
  };

  const loadedPreset = loadedPresetId ? presets.find(p => p.id === loadedPresetId) : null;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <Zap className="size-16 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-2xl font-semibold mb-2">Loading…</h2>
        </div>
      </div>
    );
  }

  // ── Render helpers ──

  const renderPresets = () => (
    <div className="relative" ref={presetDropdownRef}>
      <div className="flex items-center gap-2">
        {loadedPreset && (
          <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-primary/10 border border-primary/25 text-primary font-medium">
            <Check className="size-3" />
            {loadedPreset.name}
          </span>
        )}
        <Button
          variant="outline"
          size="sm"
          className="h-7 text-xs gap-1.5"
          onClick={() => setShowPresetDropdown(!showPresetDropdown)}
        >
          <Bookmark className="size-3" />
          {presets.length > 0 ? `Presets (${presets.length})` : 'Presets'}
          <ChevronDown className="size-3" />
        </Button>
      </div>

      {showPresetDropdown && (
        <div className="absolute right-0 top-full mt-1.5 bg-popover border border-border shadow-lg z-50 w-72">
          {/* Load section */}
          {presets.length > 0 && (
            <div className="p-2 border-b border-border">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/50 px-1 mb-1.5">Load preset</p>
              <div className="space-y-0.5">
                {presets.map(p => (
                  <div key={p.id} className="flex items-center gap-1">
                    <button
                      onClick={() => loadPreset(p)}
                      className={`flex-1 text-left px-2 py-1.5 text-xs rounded transition-colors ${
                        loadedPresetId === p.id
                          ? 'bg-primary/10 text-primary font-medium'
                          : 'text-foreground hover:bg-accent'
                      }`}
                    >
                      {loadedPresetId === p.id && <Check className="size-3 inline mr-1.5" />}
                      {p.name}
                    </button>
                    <button
                      onClick={() => handleDeletePreset(p.id)}
                      className="p-1 text-muted-foreground/40 hover:text-destructive transition-colors rounded"
                      title="Delete preset"
                    >
                      <Trash2 className="size-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Save section */}
          <div className="p-2">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/50 px-1 mb-1.5">Save current</p>
            <div className="flex items-center gap-1.5">
              <Input
                value={presetName}
                onChange={e => setPresetName(e.target.value)}
                placeholder="Preset name…"
                className="h-7 text-xs flex-1"
              />
              {loadedPresetId && loadedPreset && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs px-2 shrink-0"
                  disabled={isSavingPreset || !presetName.trim()}
                  onClick={() => handleSavePreset(true)}
                  title={`Update "${loadedPreset.name}"`}
                >
                  {isSavingPreset ? '…' : 'Update'}
                </Button>
              )}
              <Button
                size="sm"
                variant={loadedPresetId ? 'ghost' : 'default'}
                className="h-7 text-xs px-2 shrink-0"
                disabled={isSavingPreset || !presetName.trim()}
                onClick={() => handleSavePreset(false)}
              >
                {isSavingPreset ? '…' : 'Save'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderTemplates = () => (
    <div className="border border-border rounded-lg overflow-hidden">
      <div className="px-4 py-3 border-b border-border bg-muted/20">
        <div className="flex items-center gap-2">
          <FileText className="size-4 text-muted-foreground" />
          <span className="text-sm font-semibold">Templates</span>
          {selectedTemplates.length > 0 && (
            <Badge variant="secondary" className="text-[10px] ml-auto">
              {selectedTemplates.length} selected
            </Badge>
          )}
        </div>
      </div>
      <div className="p-2 space-y-1">
        {templateSets.map(ts => {
          const complete = isComplete(ts);
          const isSelected = selectedTemplateSetIds.has(ts.id);
          return (
            <label
              key={ts.id}
              className={`flex items-center gap-3 p-2.5 rounded-md border cursor-pointer transition-colors ${
                isSelected ? 'border-primary/40 bg-primary/5' : 'border-transparent hover:bg-muted/30'
              }`}
            >
              <Checkbox
                checked={isSelected}
                onCheckedChange={checked => {
                  setSelectedTemplateSetIds(prev => {
                    const next = new Set(prev);
                    checked ? next.add(ts.id) : next.delete(ts.id);
                    return next;
                  });
                }}
                className="shrink-0"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{ts.name}</p>
                <p className="text-[11px] text-muted-foreground">
                  {ts.language_code.toUpperCase()} · {ts.template_content.keywords.length} kw
                </p>
              </div>
              {!complete && (
                <span className="text-[10px] text-warning font-medium">Incomplete</span>
              )}
            </label>
          );
        })}
      </div>
      {selectedTemplates.some(ts => !isComplete(ts)) && (
        <div className="flex items-start gap-2 text-xs text-muted-foreground bg-warning/5 border-t border-warning/20 px-4 py-2.5">
          <AlertCircle className="size-3.5 mt-0.5 shrink-0 text-warning" />
          <span>Incomplete templates will have empty slots skipped.</span>
        </div>
      )}
    </div>
  );

  const renderCampaignSetup = () => (
    <div className="border border-border rounded-lg overflow-hidden">
      <button
        onClick={() => setShowCampaignSetup(!showCampaignSetup)}
        className="w-full px-4 py-3 flex items-center justify-between bg-muted/20 hover:bg-muted/30 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Settings2 className="size-4 text-muted-foreground" />
          <span className="text-sm font-semibold">Campaign Setup</span>
          {overrideEnabled && (
            <span className="text-[10px] px-1.5 py-0.5 bg-primary/10 text-primary rounded font-medium">Custom</span>
          )}
        </div>
        <ChevronDown className={`size-4 text-muted-foreground transition-transform ${showCampaignSetup ? 'rotate-180' : ''}`} />
      </button>

      {showCampaignSetup && (
        <div className="p-4 space-y-4 border-t border-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium">Custom campaign structure</p>
              <p className="text-[11px] text-muted-foreground">
                {overrideEnabled ? 'Define how campaigns and ad groups are organized' : 'Using structure from each template'}
              </p>
            </div>
            <Switch checked={overrideEnabled} onCheckedChange={setOverrideEnabled} />
          </div>

          {overrideEnabled && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Group campaigns by</Label>
                  <Select
                    value={structureOverride.primary_grouping_field || '__none__'}
                    onValueChange={v => setStructureOverride(prev => ({ ...prev, primary_grouping_field: v === '__none__' ? undefined : v }))}
                  >
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue placeholder="Auto (from template)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Auto (from template)</SelectItem>
                      {columns.map(col => (
                        <SelectItem key={col} value={col}>{col}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Split ad groups by</Label>
                  <Select
                    value={structureOverride.secondary_grouping_field || '__none__'}
                    onValueChange={v => setStructureOverride(prev => ({ ...prev, secondary_grouping_field: v === '__none__' ? undefined : v }))}
                  >
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue placeholder="No split" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">No split</SelectItem>
                      {columns.map(col => (
                        <SelectItem key={col} value={col}>{col}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Campaign name pattern</Label>
                <Input
                  ref={campaignNameInputRef}
                  value={structureOverride.campaign_name_template ?? ''}
                  onChange={e => {
                    setStructureOverride(prev => ({ ...prev, campaign_name_template: e.target.value || undefined }));
                    campaignNameCursorRef.current = e.target.selectionStart;
                  }}
                  onSelect={e => { campaignNameCursorRef.current = (e.target as HTMLInputElement).selectionStart; }}
                  onBlur={e => { campaignNameCursorRef.current = e.target.selectionStart; }}
                  placeholder={selectedTemplate?.template_content.campaign_name_template || 'e.g., {region} — Ads'}
                  className="h-8 text-sm font-mono"
                />
                <div className="flex flex-wrap gap-1">
                  {columns.slice(0, 8).map(col => (
                    <button
                      key={col}
                      type="button"
                      className="text-[10px] font-mono px-1.5 py-0.5 rounded border bg-primary/8 border-primary/20 text-primary/70 hover:bg-primary/15 transition-colors"
                      onMouseDown={e => e.preventDefault()}
                      onClick={() => {
                        const token = `{${col}}`;
                        setStructureOverride(prev => {
                          const val = prev.campaign_name_template ?? '';
                          const pos = campaignNameCursorRef.current ?? val.length;
                          const next = val.slice(0, pos) + token + val.slice(pos);
                          campaignNameCursorRef.current = pos + token.length;
                          return { ...prev, campaign_name_template: next };
                        });
                        setTimeout(() => {
                          const el = campaignNameInputRef.current;
                          if (el) { el.focus(); el.setSelectionRange(campaignNameCursorRef.current!, campaignNameCursorRef.current!); }
                        });
                      }}
                    >
                      {`{${col}}`}
                    </button>
                  ))}
                  <button
                    type="button"
                    className="text-[10px] font-mono px-1.5 py-0.5 rounded border bg-violet-500/10 border-violet-500/30 text-violet-600 dark:text-violet-400 hover:bg-violet-500/20 transition-colors"
                    onMouseDown={e => e.preventDefault()}
                    onClick={() => {
                      const token = '{template_name}';
                      setStructureOverride(prev => {
                        const val = prev.campaign_name_template ?? '';
                        const pos = campaignNameCursorRef.current ?? val.length;
                        const next = val.slice(0, pos) + token + val.slice(pos);
                        campaignNameCursorRef.current = pos + token.length;
                        return { ...prev, campaign_name_template: next };
                      });
                      setTimeout(() => {
                        const el = campaignNameInputRef.current;
                        if (el) { el.focus(); el.setSelectionRange(campaignNameCursorRef.current!, campaignNameCursorRef.current!); }
                      });
                    }}
                  >
                    {'{template_name}'}
                  </button>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Ad group name pattern</Label>
                <Input
                  ref={adGroupNameInputRef}
                  value={structureOverride.ad_group_name_template ?? ''}
                  onChange={e => {
                    setStructureOverride(prev => ({ ...prev, ad_group_name_template: e.target.value || undefined }));
                    adGroupNameCursorRef.current = e.target.selectionStart;
                  }}
                  onSelect={e => { adGroupNameCursorRef.current = (e.target as HTMLInputElement).selectionStart; }}
                  onBlur={e => { adGroupNameCursorRef.current = e.target.selectionStart; }}
                  placeholder={selectedTemplate?.template_content.ad_group_name_template || 'e.g., AG — {nom_standard}'}
                  className="h-8 text-sm font-mono"
                />
                <div className="flex flex-wrap gap-1">
                  {columns.slice(0, 8).map(col => (
                    <button
                      key={col}
                      type="button"
                      className="text-[10px] font-mono px-1.5 py-0.5 rounded border bg-primary/8 border-primary/20 text-primary/70 hover:bg-primary/15 transition-colors"
                      onMouseDown={e => e.preventDefault()}
                      onClick={() => {
                        const token = `{${col}}`;
                        setStructureOverride(prev => {
                          const val = prev.ad_group_name_template ?? '';
                          const pos = adGroupNameCursorRef.current ?? val.length;
                          const next = val.slice(0, pos) + token + val.slice(pos);
                          adGroupNameCursorRef.current = pos + token.length;
                          return { ...prev, ad_group_name_template: next };
                        });
                        setTimeout(() => {
                          const el = adGroupNameInputRef.current;
                          if (el) { el.focus(); el.setSelectionRange(adGroupNameCursorRef.current!, adGroupNameCursorRef.current!); }
                        });
                      }}
                    >
                      {`{${col}}`}
                    </button>
                  ))}
                  <button
                    type="button"
                    className="text-[10px] font-mono px-1.5 py-0.5 rounded border bg-violet-500/10 border-violet-500/30 text-violet-600 dark:text-violet-400 hover:bg-violet-500/20 transition-colors"
                    onMouseDown={e => e.preventDefault()}
                    onClick={() => {
                      const token = '{template_name}';
                      setStructureOverride(prev => {
                        const val = prev.ad_group_name_template ?? '';
                        const pos = adGroupNameCursorRef.current ?? val.length;
                        const next = val.slice(0, pos) + token + val.slice(pos);
                        adGroupNameCursorRef.current = pos + token.length;
                        return { ...prev, ad_group_name_template: next };
                      });
                      setTimeout(() => {
                        const el = adGroupNameInputRef.current;
                        if (el) { el.focus(); el.setSelectionRange(adGroupNameCursorRef.current!, adGroupNameCursorRef.current!); }
                      });
                    }}
                  >
                    {'{template_name}'}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Campaign status</Label>
                  <Select
                    value={structureOverride.campaign_status || '__none__'}
                    onValueChange={v => setStructureOverride(prev => ({ ...prev, campaign_status: v === '__none__' ? undefined : v as 'Enabled' | 'Paused' }))}
                  >
                    <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Auto (from template)" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Auto (from template)</SelectItem>
                      <SelectItem value="Enabled">Enabled</SelectItem>
                      <SelectItem value="Paused">Paused</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Ad group status</Label>
                  <Select
                    value={structureOverride.ad_group_status || '__none__'}
                    onValueChange={v => setStructureOverride(prev => ({ ...prev, ad_group_status: v === '__none__' ? undefined : v as 'Enabled' | 'Paused' }))}
                  >
                    <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Auto (from template)" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Auto (from template)</SelectItem>
                      <SelectItem value="Enabled">Enabled</SelectItem>
                      <SelectItem value="Paused">Paused</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );

  const renderBudgetSetup = () => (
    <div className="border border-border rounded-lg overflow-hidden">
      <div className="px-4 py-3 flex items-center justify-between bg-muted/20">
        <div className="flex items-center gap-2">
          <DollarSign className="size-4 text-muted-foreground" />
          <div>
            <p className="text-xs font-medium">Set campaign budget</p>
            <p className="text-[11px] text-muted-foreground">
              {budgetConfig.enabled ? 'Daily budget included in CSV' : 'No budget in CSV export'}
            </p>
          </div>
        </div>
        <Switch
          checked={budgetConfig.enabled}
          onCheckedChange={checked => setBudgetConfig(prev => ({ ...prev, enabled: checked }))}
        />
      </div>

      {budgetConfig.enabled && (
        <div className="p-4 space-y-3 border-t border-border">
          {/* Mode selector */}
          <div className="flex items-center gap-2">
            <button
              className={`text-xs px-2.5 py-1 rounded-md border transition-colors ${
                budgetConfig.mode === 'uniform'
                  ? 'bg-primary/10 border-primary/30 text-primary font-medium'
                  : 'border-border text-muted-foreground hover:text-foreground hover:bg-muted/30'
              }`}
              onClick={() => setBudgetConfig(prev => ({ ...prev, mode: 'uniform' }))}
            >
              Same for all
            </button>
            <button
              className={`text-xs px-2.5 py-1 rounded-md border transition-colors ${
                budgetConfig.mode === 'per_campaign'
                  ? 'bg-primary/10 border-primary/30 text-primary font-medium'
                  : 'border-border text-muted-foreground hover:text-foreground hover:bg-muted/30'
              }`}
              onClick={() => setBudgetConfig(prev => ({ ...prev, mode: 'per_campaign' }))}
            >
              Per campaign
            </button>
          </div>

          {budgetConfig.mode === 'uniform' && (
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Daily budget (all campaigns)</Label>
              <div className="relative">
                <Input
                  type="number"
                  min={0}
                  step="0.01"
                  value={budgetConfig.uniformAmount ?? ''}
                  onChange={e => setBudgetConfig(prev => ({
                    ...prev,
                    uniformAmount: e.target.value ? parseFloat(e.target.value) : undefined,
                  }))}
                  placeholder="e.g., 20.00"
                  className="h-8 text-sm font-mono"
                />
              </div>
            </div>
          )}

          {budgetConfig.mode === 'per_campaign' && (
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Daily budget per campaign</Label>
              {uniqueCampaignNames.length === 0 ? (
                <p className="text-[11px] text-muted-foreground/60 italic py-2">
                  Campaign names will be determined after generation — configure a grouping field and campaign name pattern first.
                </p>
              ) : (
                <div className="max-h-60 overflow-y-auto space-y-1.5 border border-border rounded-md p-2">
                  {uniqueCampaignNames.map(name => (
                    <div key={name} className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground truncate flex-1 min-w-0">{name}</span>
                      <Input
                        type="number"
                        min={0}
                        step="0.01"
                        value={budgetConfig.perCampaignAmounts?.[name] ?? ''}
                        onChange={e => setBudgetConfig(prev => ({
                          ...prev,
                          perCampaignAmounts: {
                            ...prev.perCampaignAmounts,
                            [name]: e.target.value ? parseFloat(e.target.value) : undefined as any,
                          },
                        }))}
                        placeholder="0.00"
                        className="h-7 text-xs font-mono w-24 shrink-0"
                      />
                    </div>
                  ))}
                </div>
              )}
              {uniqueCampaignNames.length > 3 && (
                <p className="text-[10px] text-muted-foreground/50">
                  {uniqueCampaignNames.length} campaigns
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderAllLocationsTab = () => (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          Generate ads for {bulkEnabled ? 'all' : 'no'} locations in your database
          {bulkEnabled && rows.length > 0 && <span className="text-foreground font-medium"> ({rows.length.toLocaleString()} total)</span>}
        </p>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-muted-foreground">{bulkEnabled ? 'On' : 'Off'}</span>
          <Switch checked={bulkEnabled} onCheckedChange={setBulkEnabled} />
        </div>
      </div>

      {bulkEnabled && (
        <>
          {/* Filter */}
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Filter by field</Label>
            <div className="flex gap-2">
              <Select
                value={filterColumn}
                onValueChange={v => setFilterColumn(v === '__none__' ? '' : v)}
              >
                <SelectTrigger className="h-8 text-sm">
                  <Filter className="size-3.5 mr-1.5 text-muted-foreground" />
                  <SelectValue placeholder="All rows — no filter" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">All rows — no filter</SelectItem>
                  {columns.map(col => (
                    <SelectItem key={col} value={col}>{col}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {filterColumn && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 px-2 text-muted-foreground"
                  onClick={() => setFilterColumn('')}
                >
                  <X className="size-3.5" />
                </Button>
              )}
            </div>
          </div>

          {/* Value checklist */}
          {filterColumn && sortedValues.length > 0 && (
            <div className="border rounded-md overflow-hidden">
              <div className="flex items-center justify-between px-3 py-2 border-b bg-muted/30">
                <div className="flex items-center gap-3 text-xs">
                  <button
                    className="text-primary hover:underline underline-offset-2 disabled:opacity-40 disabled:no-underline"
                    onClick={() => setSelectedValues(new Set(Array.from(valueCounts.keys())))}
                    disabled={allSelected}
                  >
                    All
                  </button>
                  <span className="text-muted-foreground">·</span>
                  <button
                    className="text-primary hover:underline underline-offset-2 disabled:opacity-40 disabled:no-underline"
                    onClick={() => setSelectedValues(new Set())}
                    disabled={noneSelected}
                  >
                    None
                  </button>
                </div>
                {sortedValues.length > 8 && (
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 size-3 text-muted-foreground" />
                    <Input
                      value={valueSearch}
                      onChange={e => setValueSearch(e.target.value)}
                      placeholder="Search…"
                      className="h-6 pl-6 pr-2 text-xs w-32"
                    />
                  </div>
                )}
              </div>
              <div className="max-h-52 overflow-y-auto">
                {visibleValues.length === 0 ? (
                  <p className="text-xs text-muted-foreground px-3 py-4 text-center">No matches</p>
                ) : (
                  visibleValues.map(([value, count]) => (
                    <label
                      key={value}
                      className="flex items-center gap-2.5 px-3 py-1.5 hover:bg-muted/40 cursor-pointer"
                    >
                      <Checkbox
                        checked={selectedValues.has(value)}
                        onCheckedChange={() => toggleValue(value)}
                        className="shrink-0"
                      />
                      <span className="text-sm flex-1 truncate">{value}</span>
                      <span className="text-xs text-muted-foreground tabular-nums shrink-0">{count.toLocaleString()}</span>
                    </label>
                  ))
                )}
              </div>
              <div className="px-3 py-2 border-t bg-muted/20 text-xs text-muted-foreground">
                {selectedValues.size.toLocaleString()} of {valueCounts.size.toLocaleString()} values
                {' · '}
                <span className={bulkRows.length === 0 && totalCustomGroupRows === 0 ? 'text-destructive font-medium' : 'text-foreground font-medium'}>
                  {bulkRows.length.toLocaleString()} rows
                </span>
              </div>
            </div>
          )}

          {/* Exclude rows */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label className="text-xs text-muted-foreground">Exclude rows</Label>
              {excludedRows.size > 0 && (
                <button
                  className="text-[10px] text-muted-foreground hover:text-foreground transition-colors"
                  onClick={() => setExcludedRows(new Set())}
                >
                  Clear all
                </button>
              )}
            </div>
            <div className="relative" ref={excludeSearchRef}>
              <div className="relative">
                <Ban className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground pointer-events-none" />
                <Input
                  value={excludeSearchQuery}
                  onChange={e => {
                    setExcludeSearchQuery(e.target.value);
                    setShowExcludeSearch(true);
                  }}
                  onFocus={() => {
                    if (excludeSearchQuery) setShowExcludeSearch(true);
                  }}
                  placeholder="Search rows to exclude…"
                  className="h-7 text-xs pl-8"
                />
                {excludeSearchQuery && (
                  <button
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    onClick={() => { setExcludeSearchQuery(''); setShowExcludeSearch(false); }}
                  >
                    <X className="size-3" />
                  </button>
                )}
              </div>
              {showExcludeSearch && getExcludeSearchResults(excludeSearchQuery).length > 0 && (
                <div className="absolute top-full mt-1 left-0 right-0 bg-popover border border-border rounded-md shadow-md z-50 overflow-hidden">
                  {getExcludeSearchResults(excludeSearchQuery).map((row, i) => {
                    const hint = rowMatchHint(row, excludeSearchQuery);
                    return (
                      <button
                        key={i}
                        className="w-full text-left px-3 py-2 text-xs hover:bg-accent transition-colors border-b border-border/30 last:border-0 flex items-center justify-between gap-2"
                        onMouseDown={e => e.preventDefault()}
                        onClick={() => excludeRow(row)}
                      >
                        <span className="truncate">{getRowLabel(row)}{hint && <span className="ml-1.5 text-primary/70">— {hint}</span>}</span>
                        <Ban className="size-3 text-muted-foreground shrink-0" />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            {excludedRows.size > 0 && (
              <div className="flex flex-wrap gap-1">
                {Array.from(excludedRows).map((row, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center gap-1 text-[11px] bg-destructive/8 border border-destructive/20 text-destructive/80 rounded px-1.5 py-0.5"
                  >
                    {getRowLabel(row)}
                    <button
                      className="text-destructive/40 hover:text-destructive transition-colors ml-0.5"
                      onClick={() => unexcludeRow(row)}
                    >
                      <X className="size-2.5" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {!filterColumn && (
            <p className="text-xs text-muted-foreground py-2">
              {excludedRows.size > 0 ? (
                <>
                  <span className="font-medium text-foreground">{bulkRows.length.toLocaleString()}</span> rows will be generated
                  <span className="text-muted-foreground/70"> ({excludedRows.size} excluded)</span>
                </>
              ) : (
                <>All <span className="font-medium text-foreground">{bulkRows.length.toLocaleString()}</span> rows will be generated.</>
              )}
              {totalCustomGroupRows > 0 && (
                <span className="text-muted-foreground/70"> ({totalCustomGroupRows} in pinned campaigns, excluded from bulk)</span>
              )}
            </p>
          )}
        </>
      )}

      {!bulkEnabled && (
        <p className="text-xs text-muted-foreground/60 py-4 text-center italic">
          Bulk generation is off — only pinned campaigns will be generated.
        </p>
      )}
    </div>
  );

  const renderPinnedCampaignsTab = () => (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          Hand-pick locations into dedicated campaigns
        </p>
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={addCustomGroup}>
          <Plus className="size-3 mr-1" />
          Add Campaign
        </Button>
      </div>

      {customGroups.length === 0 ? (
        <div className="text-center py-10 border border-dashed border-border rounded-lg">
          <Pin className="size-6 mx-auto text-muted-foreground/30 mb-2" />
          <p className="text-sm text-muted-foreground">No pinned campaigns yet</p>
          <p className="text-xs text-muted-foreground/60 mt-1 max-w-xs mx-auto">
            Pin specific locations to generate them in their own dedicated campaign, separate from bulk generation.
          </p>
          <Button size="sm" variant="ghost" className="mt-3 text-xs" onClick={addCustomGroup}>
            <Plus className="size-3 mr-1" />
            Add Campaign
          </Button>
        </div>
      ) : (
        <div className="space-y-2.5">
          {customGroups.map((group, idx) => {
            const searchResults = getSearchResults(group.searchQuery, group.id);
            const isSearchOpen = openGroupSearchId === group.id && searchResults.length > 0;
            return (
              <div key={group.id} className="border border-border rounded-lg overflow-hidden">
                {/* Campaign header */}
                <div className="px-3 py-2.5 bg-muted/20 flex items-center gap-2">
                  <span className="text-[10px] font-mono text-muted-foreground/40 shrink-0 w-4 text-center">{idx + 1}</span>
                  <Input
                    value={group.name}
                    onChange={e => updateGroup(group.id, { name: e.target.value })}
                    placeholder="Campaign name…"
                    className="h-7 text-sm font-medium border-0 bg-transparent shadow-none p-0 focus-visible:ring-0 focus-visible:shadow-none"
                  />
                  <span className="text-[10px] text-muted-foreground shrink-0">{group.rows.length} row{group.rows.length !== 1 ? 's' : ''}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-7 shrink-0 text-muted-foreground/40 hover:text-destructive"
                    onClick={() => removeGroup(group.id)}
                  >
                    <Trash2 className="size-3" />
                  </Button>
                </div>

                <div className="p-3 space-y-2.5">
                  {/* Campaign name override */}
                  <div className="space-y-1.5">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={group.useOverride}
                        onCheckedChange={checked => updateGroup(group.id, { useOverride: !!checked })}
                      />
                      <span className="text-[11px] text-muted-foreground">Custom campaign name</span>
                    </label>
                    {group.useOverride && (
                      <>
                        <Input
                          value={group.campaignNameOverride}
                          onChange={e => updateGroup(group.id, { campaignNameOverride: e.target.value })}
                          placeholder="e.g., TOP — {region_nom}"
                          className="h-7 text-xs"
                        />
                        {(selectedTemplate?.template_content.selected_fields ?? []).length > 0 && (
                          <div className="flex flex-wrap gap-1 items-center">
                            {(selectedTemplate?.template_content.selected_fields ?? []).map(f => (
                              <button
                                key={f}
                                type="button"
                                className="text-[10px] font-mono px-1.5 py-0.5 rounded border bg-primary/8 border-primary/20 text-primary/70 hover:bg-primary/15 transition-colors"
                                onClick={() => updateGroup(group.id, {
                                  campaignNameOverride: group.campaignNameOverride + `{${f.replace('custom:', '')}}`,
                                })}
                              >
                                {'{' + f.replace('custom:', '') + '}'}
                              </button>
                            ))}
                          </div>
                        )}
                        {group.campaignNameOverride.trim() && group.rows.length > 0 && (
                          <p className="text-[11px] font-mono text-primary/70 bg-primary/5 border border-primary/15 rounded px-2 py-1 truncate">
                            {replacePlaceholders(group.campaignNameOverride.trim(), group.rows[0], selectedTemplate?.template_content.template_placeholders)}
                          </p>
                        )}
                      </>
                    )}
                  </div>

                  {/* Row picker */}
                  <div
                    className="relative"
                    ref={el => { searchRefs.current[group.id] = el; }}
                  >
                    <div className="relative">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground pointer-events-none" />
                      <Input
                        value={group.searchQuery}
                        onChange={e => {
                          updateGroup(group.id, { searchQuery: e.target.value });
                          setOpenGroupSearchId(group.id);
                        }}
                        onFocus={() => {
                          if (group.searchQuery) setOpenGroupSearchId(group.id);
                        }}
                        placeholder="Search locations to add…"
                        className="h-7 text-xs pl-8"
                      />
                      {group.searchQuery && (
                        <button
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          onClick={() => updateGroup(group.id, { searchQuery: '' })}
                        >
                          <X className="size-3" />
                        </button>
                      )}
                    </div>
                    {isSearchOpen && (
                      <div className="absolute top-full mt-1 left-0 right-0 bg-popover border border-border rounded-md shadow-md z-50 overflow-hidden">
                        {searchResults.map((row, i) => {
                          const hint = rowMatchHint(row, group.searchQuery);
                          return (
                          <button
                            key={i}
                            className="w-full text-left px-3 py-2 text-xs hover:bg-accent transition-colors border-b border-border/30 last:border-0 flex items-center justify-between gap-2"
                            onMouseDown={e => e.preventDefault()}
                            onClick={() => addRowToGroup(group.id, row)}
                          >
                            <span className="truncate">{getRowLabel(row)}{hint && <span className="ml-1.5 text-primary/70">— {hint}</span>}</span>
                            <Plus className="size-3 text-muted-foreground shrink-0" />
                          </button>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Selected rows */}
                  {group.rows.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {group.rows.map((row, i) => (
                        <span
                          key={i}
                          className="inline-flex items-center gap-1 text-[11px] bg-primary/8 border border-primary/20 text-primary/80 rounded px-1.5 py-0.5"
                        >
                          {getRowLabel(row)}
                          <button
                            className="text-primary/40 hover:text-destructive transition-colors ml-0.5"
                            onClick={() => removeRowFromGroup(group.id, row)}
                          >
                            <X className="size-2.5" />
                          </button>
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-[11px] text-muted-foreground/50 italic">Search above to add locations</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  // ── Summary line ──
  const renderSummary = () => {
    const parts: string[] = [];
    if (bulkRows.length > 0) parts.push(`${bulkRows.length.toLocaleString()} locations (bulk)`);
    if (totalCustomGroupRows > 0) {
      const campaigns = customGroups.filter(g => g.rows.length > 0).length;
      parts.push(`${totalCustomGroupRows} in ${campaigns} pinned campaign${campaigns !== 1 ? 's' : ''}`);
    }
    const templateLabel = selectedTemplates.length === 1
      ? `1 template`
      : `${selectedTemplates.length} templates`;

    return (
      <div className="text-xs text-muted-foreground">
        {parts.length > 0 ? (
          <>
            <span className="text-foreground font-medium">{templateLabel}</span>
            {' × '}
            {parts.join(' + ')}
          </>
        ) : (
          <span className="text-muted-foreground/50">Select templates and data to generate</span>
        )}
      </div>
    );
  };

  return (
    <div className="app-enter">
      {/* ── Header ── */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold mb-1">Generate</h1>
          <p className="text-sm text-muted-foreground">
            Combine templates with your data to create Google Ads campaigns.
          </p>
        </div>
        {!isLoading && renderPresets()}
      </div>

      {/* ── Two-column layout ── */}
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(260px,35%)_1fr] gap-5 items-start">
        {/* Left column: Templates + Campaign Setup */}
        <div className="space-y-4">
          {renderTemplates()}
          {selectedTemplates.length > 0 && renderCampaignSetup()}
          {selectedTemplates.length > 0 && renderBudgetSetup()}
        </div>

        {/* Right column: Targeting */}
        {selectedTemplates.length > 0 && (
          <div className="border border-border rounded-lg overflow-hidden">
            {/* Tab bar */}
            <div className="flex border-b border-border bg-muted/20">
              <button
                onClick={() => setTargetingTab('all')}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold transition-colors relative ${
                  targetingTab === 'all'
                    ? 'text-primary'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Globe className="size-3.5" />
                All Locations
                {bulkEnabled && bulkRows.length > 0 && (
                  <span className="text-[10px] font-normal ml-1 opacity-60">{bulkRows.length.toLocaleString()}</span>
                )}
                {targetingTab === 'all' && (
                  <span className="absolute bottom-0 left-0 right-0 h-[2px] bg-primary" />
                )}
              </button>
              <button
                onClick={() => setTargetingTab('pinned')}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold transition-colors relative ${
                  targetingTab === 'pinned'
                    ? 'text-primary'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Pin className="size-3.5" />
                Pinned Campaigns
                {customGroups.length > 0 && (
                  <span className="text-[10px] font-normal ml-1 opacity-60">{customGroups.length}</span>
                )}
                {targetingTab === 'pinned' && (
                  <span className="absolute bottom-0 left-0 right-0 h-[2px] bg-primary" />
                )}
              </button>
            </div>

            {/* Tab content */}
            <div className="p-4">
              {targetingTab === 'all' ? renderAllLocationsTab() : renderPinnedCampaignsTab()}
            </div>

            {/* Summary footer */}
            <div className="px-4 py-2.5 border-t border-border bg-muted/10">
              {renderSummary()}
            </div>
          </div>
        )}

        {selectedTemplates.length === 0 && (
          <div className="border border-dashed border-border rounded-lg flex items-center justify-center py-16 text-center">
            <div>
              <MapPin className="size-8 mx-auto text-muted-foreground/30 mb-2" />
              <p className="text-sm text-muted-foreground">Select a template to configure targeting</p>
            </div>
          </div>
        )}
      </div>

      {/* ── Note ── */}
      <p className="text-[11px] text-muted-foreground/60 mt-4 px-1">
        Callout and sitelink extensions are configured in the template but not included in the CSV export — manage these account-wide in Google Ads.
      </p>

      {/* ── Generate button ── */}
      <Button
        size="lg"
        className="w-full mt-5"
        disabled={!canGenerate}
        onClick={handleGeneratePreview}
      >
        {selectedTemplates.length > 1 ? `Generate ${selectedTemplates.length} Templates` : 'Generate Preview'}
        {' '}
        {bulkRows.length > 0 && totalCustomGroupRows > 0
          ? `— ${bulkRows.length.toLocaleString()} bulk + ${totalCustomGroupRows.toLocaleString()} pinned`
          : bulkRows.length > 0
          ? `— ${bulkRows.length.toLocaleString()} rows`
          : totalCustomGroupRows > 0
          ? `— ${totalCustomGroupRows.toLocaleString()} pinned rows`
          : ''}
        <ChevronRight className="size-4 ml-1" />
      </Button>
    </div>
  );
}
